import React, { useState } from 'react';

interface Template {
  id: string;
  name: string;
  description: string;
  category?: string;
  colors?: {
    primary: string;
    accent: string;
    background: string;
  };
}

interface TemplateSelectorProps {
  templates: Template[];
  selectedTemplate: string;
  onSelect: (templateId: string) => void;
}

export const TemplateSelector: React.FC<TemplateSelectorProps> = ({
  templates,
  selectedTemplate,
  onSelect,
}) => {
  const [hoveredTemplate, setHoveredTemplate] = useState<string | null>(null);

  return (
    <div className="mb-10">
      <div className="flex items-center gap-3 mb-6">
        <h2 className="text-2xl font-bold text-slate-900">选择风格</h2>
        <div className="h-px flex-1 bg-gradient-to-r from-slate-200 to-transparent"></div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
        {templates.map((template) => {
          const isSelected = selectedTemplate === template.id;
          const isHovered = hoveredTemplate === template.id;

          return (
            <button
              key={template.id}
              type="button"
              onClick={() => onSelect(template.id)}
              onMouseEnter={() => setHoveredTemplate(template.id)}
              onMouseLeave={() => setHoveredTemplate(null)}
              className={`
                group relative p-6 rounded-2xl text-left transition-all duration-300
                transform hover:scale-[1.02] active:scale-[0.98]
                ${isSelected
                  ? 'shadow-xl shadow-slate-200/50'
                  : 'shadow-md hover:shadow-lg shadow-slate-100'
                }
              `}
              style={{
                background: isSelected || isHovered
                  ? `linear-gradient(135deg, ${template.colors?.primary || '#3B82F6'}15 0%, ${template.colors?.accent || '#8B5CF6'}15 100%)`
                  : 'white',
                border: isSelected ? `2px solid ${template.colors?.primary || '#3B82F6'}` : '2px solid transparent',
              }}
            >
              {/* 选中指示器 */}
              {isSelected && (
                <div className="absolute top-4 right-4 w-6 h-6 rounded-full flex items-center justify-center text-white text-sm"
                  style={{ backgroundColor: template.colors?.primary }}>
                  ✓
                </div>
              )}

              {/* 类别标签 */}
              {template.category && (
                <div className="inline-block px-2.5 py-1 rounded-full text-xs font-medium mb-3 uppercase tracking-wide"
                  style={{
                    backgroundColor: `${template.colors?.primary || '#3B82F6'}15`,
                    color: template.colors?.primary || '#3B82F6',
                  }}>
                  {template.category}
                </div>
              )}

              {/* 标题 */}
              <h3 className="text-xl font-bold text-slate-900 mb-2 group-hover:text-transparent group-hover:bg-clip-text group-hover:bg-gradient-to-r group-hover:from-slate-900 group-hover:to-slate-600 transition-all">
                {template.name}
              </h3>

              {/* 描述 */}
              <p className="text-slate-600 text-sm mb-4 leading-relaxed">
                {template.description}
              </p>

              {/* 颜色预览 */}
              {template.colors && (
                <div className="flex items-center gap-3 mt-auto">
                  <div className="flex gap-2">
                    <div
                      className="w-8 h-8 rounded-xl border-2 border-white shadow-sm transition-transform group-hover:scale-110"
                      style={{ backgroundColor: template.colors.primary }}
                      title="主色"
                    />
                    <div
                      className="w-8 h-8 rounded-xl border-2 border-white shadow-sm transition-transform group-hover:scale-110"
                      style={{ backgroundColor: template.colors.accent }}
                      title="强调色"
                    />
                    <div
                      className="w-8 h-8 rounded-xl border-2 border-white shadow-sm transition-transform group-hover:scale-110"
                      style={{ backgroundColor: template.colors.background }}
                      title="背景色"
                    />
                  </div>

                  {/* 装饰性渐变条 */}
                  <div className="flex-1 h-1.5 rounded-full opacity-30"
                    style={{
                      background: `linear-gradient(90deg, ${template.colors.primary}, ${template.colors.accent})`,
                    }}
                  />
                </div>
              )}

              {/* 悬停效果装饰 */}
              <div className={`
                absolute inset-0 rounded-2xl opacity-0 transition-opacity duration-300
                ${isHovered ? 'opacity-100' : ''}
              `}
                style={{
                  background: `linear-gradient(135deg, ${template.colors?.accent || '#8B5CF6'}08, transparent)`,
                  pointerEvents: 'none',
                }}
              />
            </button>
          );
        })}
      </div>
    </div>
  );
};
